import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useBusinessServiceRequest, useCurrentUser } from '@ingenico/react-section-utils';
import { useUserDateTimeFormat } from '@ingenico/react-utils/hooks';
import { isEmpty } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LogicalDeviceLocationGeofencing } from '../components/logical-device-location-card/logicalDeviceLocationCard.model';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';
import type { DeviceAlerts } from '../model/deviceAlerts.model';
import { getDeviceGeofencingDetails } from '../utils/deviceAlerts.util';
import { useDeviceManagementCapability } from './useDeviceManagementCapability.hook';

export function useLogicalDeviceGeofendingDetails(
  logicalDeviceId: string,
): LogicalDeviceLocationGeofencing | undefined {
  const hasGeofencingCapability = useDeviceManagementCapability('logicalDevice.enableGeofencing');
  const currentUser = useCurrentUser();
  const { t, i18n } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);

  const deviceAlertsQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService) =>
        !isEmpty(logicalDeviceId) && hasGeofencingCapability
          ? deviceManagementService.getDeviceAlertsById(logicalDeviceId)
          : null,
      [hasGeofencingCapability, logicalDeviceId],
    ),
  );

  const isGeofencingEnabledByRule = useMemo(
    () => deviceAlertsQuery.data?.isEnabled,
    [deviceAlertsQuery.data?.isEnabled],
  );

  const { getUserDateTimeFormatWithTimezone } = useUserDateTimeFormat(currentUser?.dateTimeFormat);

  const formatDate = useCallback(
    (instant?: string) => getUserDateTimeFormatWithTimezone({ instant, timezone: currentUser?.zoneInfo }),
    [getUserDateTimeFormatWithTimezone, currentUser?.zoneInfo],
  );

  const translateGeofencingAreas = useCallback(
    (data: DeviceAlerts) => {
      return {
        ...data,
        areas:
          data.areas?.map((area) =>
            i18n.exists(`geofenceAreas.${area}`, { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })
              ? t(`geofenceAreas.${area}`, { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })
              : area,
          ) ?? [],
      };
    },
    [i18n, t],
  );

  return useMemo(
    () =>
      isGeofencingEnabledByRule && hasGeofencingCapability && deviceAlertsQuery.data
        ? getDeviceGeofencingDetails(translateGeofencingAreas(deviceAlertsQuery.data), formatDate)
        : undefined,
    [isGeofencingEnabledByRule, hasGeofencingCapability, deviceAlertsQuery.data, translateGeofencingAreas, formatDate],
  );
}
